import React from 'react';
import { APIError } from './APIClient';
function getSentry() {
    return window.Sentry;
}
export var CSRFCodes;
(function (CSRFCodes) {
    CSRFCodes[CSRFCodes["MissingHeader"] = -1000] = "MissingHeader";
    CSRFCodes[CSRFCodes["MissingCookie"] = -999] = "MissingCookie";
    CSRFCodes[CSRFCodes["Mismatch"] = -998] = "Mismatch";
})(CSRFCodes || (CSRFCodes = {}));
export function isAPIErrorResponse(res) {
    return typeof res === 'object' && !!res.errors;
}
function buildDetails(err) {
    const extra = {
        Error: { ...err },
    };
    Object.keys(err).forEach(async (k) => {
        const val = err[k];
        // tslint:disable:next-line no-unsafe-any
        extra[k] = val;
        return;
    });
    return extra;
}
class NonErrorError extends Error {
    constructor(value) {
        super(`Received non error object ${typeof value} ${String(value)}`);
        this.value = value;
    }
}
function submitError(err, display) {
    const err2 = err instanceof Error ? err : new NonErrorError(err);
    let id;
    try {
        const extra = buildDetails(err2);
        const sntry = getSentry();
        if (sntry) {
            sntry.withScope(scope => {
                scope.setExtras(extra);
                id = sntry.captureException(err2);
            });
            return id;
        }
        else {
            display(React.createElement(React.Fragment, null, "Unknown error occured, please contact host"));
        }
        console.error(err2, extra);
    }
    catch (fatalErr) {
        console.error('Failed to log', err, err2, fatalErr);
    }
    return;
}
function showErrorNotification(msg, display) {
    display(React.createElement("div", null,
        React.createElement("p", null,
            React.createElement("strong", null, "One or more errors occurred while trying to process your request.")),
        msg));
}
function showErrorsListed({ errors }, display, errorMessageMap) {
    const list = [];
    const append = (str) => {
        list.push(React.createElement("li", { key: list.length }, str));
    };
    const { validation, access, logic, usage, resource, authentication, server, rateLimit, internal, } = errors;
    if (validation) {
        validation.forEach(err => {
            append(err.message);
        });
    }
    if (access) {
        append(access.permissions ? `Missing permissions: ${access.permissions.join(',')}` : access.message);
    }
    if (logic) {
        logic.forEach(err => {
            append(err.message);
            if (err.args) {
                append(Object.keys(err.args).reduce((msg, key) => msg + `${key}: ${err.args[key]}`, ''));
            }
        });
    }
    if (server) {
        append(`Our server ran into an issue! We already logged it but feel free to poke us with the following: ${server.hash}!`);
    }
    if (usage) {
        throw new Error(`Usage Error ${usage}`);
    }
    if (resource) {
        append(resource.message || `Unable to find this ${resource.name}`);
    }
    if (authentication) {
        const custom = errorMessageMap[authentication.code];
        const msg = !!custom ?
            typeof custom === 'function' ?
                custom() :
                custom :
            React.createElement(React.Fragment, null,
                authentication.message,
                " (",
                authentication.code,
                ")");
        list.push(React.createElement("li", null, msg));
    }
    if (rateLimit) {
        append(`${rateLimit.message}, try again in ${Math.ceil(rateLimit.expiresAt - Date.now() / 1000)} seconds!`);
    }
    if (internal) {
        internal.forEach(err => append(err));
    }
    showErrorNotification(React.createElement("ul", null, list), display);
}
export function makeReporter({ showError, errorMessages }) {
    return function captureError(err) {
        if (err instanceof APIError) {
            showErrorsListed(err.apiResponse, showError, errorMessages);
            return;
        }
        return submitError(err, showError);
    };
}
export function setUserContext(userId) {
    const sntry = getSentry();
    if (sntry) {
        sntry.setUser({
            id: userId.toString(),
        });
    }
}
export function showReportDialog(eventId) {
    const sntry = getSentry();
    if (!sntry) {
        return;
    }
    sntry.showReportDialog({
        errorGeneric: 'An error occurred while submitting your report to Sentry. Try again in a few moments.',
        eventId,
        subtitle: 'We have already logged the error, but additional information will help us resolve the issue quicker.',
        subtitle2: 'Please provide as much information on what happened as you feel would be helpful.',
        successMessage: 'Thanks for your help! We have attached your additional info to the error ticket.',
        title: `Error Reporting – ${eventId}`,
    });
}
//# sourceMappingURL=errorHandling.js.map